import { useClientPortalAPI, useQueryParams } from "@certn/api";
import { Text, Stack, Link } from "@certn/cascadia";
import {
  BasicButton,
  Container,
  Layout,
  Loader,
  MediaQuery,
  Pagination,
} from "@certn/cascadia-react";
import {
  orderingParamToSortDescriptor,
  sortDescriptorToOrderingParam,
} from "@certn/cascadia-react/components/data-tables/utils";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { BillingProfilesTable } from "./BillingProfilesTable";
import { PageTitle } from "./components/PageTitle";
import { TableDataCard } from "./components/TableDataCard";
import { CurrencyFilter } from "./table-filters/CurrencyFilter";
import { PayerFilter } from "./table-filters/PayerFilter";
import { StatusFilter } from "./table-filters/StatusFilter";
import { AppFooterLinks } from "../../components/app-footer/AppFooterLinks";
import { AppHeader } from "../../components/app-header/AppHeader";
import { ClearFilters } from "../../components/filters/ClearFilters";
import { FilterBar } from "../../components/filters/FilterBar";
import { usePermissions } from "../../components/permissions/PermissionsProvider";
import { useClientAccount } from "../../hooks/useClientAccount";
import { BillingNavigation } from "../../routes/billing/BillingNavigation";

export function BillingProfilesPage() {
  const navigate = useNavigate();
  const { client } = useClientAccount();
  const { flatParams, setParam } = useQueryParams();
  const { data, isLoading } = useClientPortalAPI(
    `/api/internal/client-portal/customer-accounts/${client?.id}/billing-profiles/`,
    {
      page: flatParams?.page,
      page_size: flatParams?.page_size,
      ordering: flatParams?.ordering,
      currency: flatParams?.currency,
      archived: flatParams?.archived || "ACTIVE",
      payer: flatParams?.payer,
    }
  );
  const { hasPermission } = usePermissions();

  const userHasBillingProfileCreatePermission = hasPermission({
    action: "create",
    target: "billing_profile",
    scope: "all",
  });

  return (
    <Layout
      layout="wide"
      header={<AppHeader />}
      navigation={<BillingNavigation />}
      footer={<AppFooterLinks />}
    >
      <Container layout="full-width">
        <PageTitle
          actions={
            userHasBillingProfileCreatePermission && (
              <BasicButton
                onPress={() => {
                  navigate("create");
                }}
              >
                {t("New billing profile")}
              </BasicButton>
            )
          }
        >
          <Trans>Billing Profiles</Trans>
        </PageTitle>
        <MediaQuery sm type="hide">
          <Stack orientation="horizontal" gap="space-2">
            <Text color="text-weak">
              <Trans>
                Enable more flexible billing with custom billing profiles.
                Tailor invoicing preferences, view groups assigned to each
                billing profile, and set unique contact, and address details.
              </Trans>
            </Text>
            <Link
              target="_blank"
              href="https://portal.certn.co/hc/en-us/articles/29007204255507-Billing-profiles-invoices-and-payments#h_01HWXFX3M4XGJJFNQBM9PRA8EF"
              textStyle="global/body-secondary"
              linkStyle="monochrome"
              testID="billing-profile-learn-more-link"
            >
              <Trans>Learn more</Trans>
            </Link>
          </Stack>
        </MediaQuery>
        <FilterBar testID="billing-profile">
          <CurrencyFilter isLoading={isLoading} />
          <StatusFilter isLoading={isLoading} />
          {client?.applicant_pay_enabled ? (
            <PayerFilter isLoading={isLoading} />
          ) : null}
          <ClearFilters />
        </FilterBar>
        <MediaQuery sm type="hide">
          <BillingProfilesTable
            onSortChange={(sortDescriptor) => {
              setParam(
                "ordering",
                sortDescriptorToOrderingParam(sortDescriptor)
              );
            }}
            sortDescriptor={orderingParamToSortDescriptor(
              flatParams["ordering"]
            )}
            results={data?.results}
            isLoading={isLoading}
            showPayerColumn={Boolean(client?.applicant_pay_enabled)}
          />
        </MediaQuery>
        <MediaQuery sm type="show">
          <Container layout="full-width">
            {!isLoading ? (
              data?.results &&
              data.results.map((billingProfile, i) => {
                return (
                  <TableDataCard
                    billingProfileName={billingProfile.profile_name || ""}
                    billingProfileId={billingProfile.id || ""}
                    companyName={client?.auth0_display_name || ""}
                    currency={billingProfile.currency || ""}
                    groups={billingProfile.customergroup_set || []}
                    archived={billingProfile.archived}
                    key={i}
                  />
                );
              })
            ) : (
              <Loader />
            )}
          </Container>
        </MediaQuery>
        <Pagination
          total={data?.count ? data.count : 0}
          page={flatParams["page"] ? Number(flatParams["page"]) : 1}
          pageCount={
            flatParams["page_size"] ? Number(flatParams["page_size"]) : 10
          }
        />
      </Container>
    </Layout>
  );
}
